import { useParams } from 'react-router-dom';
import { Refresh } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Integration_GetIntegrationsByPortalIdDocument,
  useIntegration_DeleteIntegrationByIdMutation,
  useIntegration_RefreshIntegrationOAuthTokenByIntegrationIdMutation,
} from 'queries';

import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import { useStyles } from './integration-actions.styles';

type Props = {
  integrationId: string;
};

export const IntegrationActions = ({ integrationId }: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const [refreshIntegrationOAuthToken] =
    useIntegration_RefreshIntegrationOAuthTokenByIntegrationIdMutation({
      refetchQueries: [
        {
          query: Integration_GetIntegrationsByPortalIdDocument,
          variables: {
            input: {
              portalId: portalId,
            },
          },
        },
      ],
      onCompleted: () => {
        showAlert({
          message: 'OAuth token has been refreshed.',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'An issue occurred refreshing your OAuth token: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [deleteIntegration] = useIntegration_DeleteIntegrationByIdMutation({
    refetchQueries: [
      {
        query: Integration_GetIntegrationsByPortalIdDocument,
        variables: {
          input: {
            portalId: portalId,
          },
        },
      },
    ],
    onCompleted: () => {
      showAlert({
        message: 'Integration has been deleted.',
        severity: NotificationSeverity.SUCCESS,
      });
    },
    onError: (e) => {
      showAlert({
        message: 'Error deleting selected integration: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleRefreshToken = () => {
    refreshIntegrationOAuthToken({
      variables: {
        input: {
          integrationId,
          portalId,
        },
      },
    });
  };

  const handleDeleteToken = () => {
    if (!window.confirm('Are you sure you want to delete this integration?')) {
      return;
    }

    deleteIntegration({
      variables: {
        input: {
          integrationId,
          portalId,
        },
      },
    });
  };

  return (
    <Box>
      <Tooltip title="Refresh Token" key="refreshToken">
        <IconButton aria-label="refreshToken" onClick={() => handleRefreshToken()} size="medium">
          <Refresh />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete Token" key="deleteToken">
        <IconButton aria-label="deleteToken" onClick={() => handleDeleteToken()} size="medium">
          <DeleteIcon className={classes.redButtonColor} fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
